import { useEffect, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { InputNumber } from 'antd';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import {
  Asset,
  InsurancePremiumMode,
} from '../../../../../../../types/apiTypes';
import {
  formatCurrencyForAntd,
  parseCurrencyFromAntd,
} from '../../../../../../../utils/currencyFormat';
import {
  FLEET_AMOUNT_LABEL,
  FLEET_INSURANCE_MODAL_TITLE,
  FLEET_OPTION_LABEL,
  MODE_QUESTION_LABEL,
  PER_ASSET_OPTION_DESCRIPTION,
  PER_ASSET_OPTION_LABEL,
  centsToDollars,
  dollarsToCents,
  fleetModeExplainer,
  perAssetSwitchWarning,
  sumAssetInsuranceCents,
} from './fleetInsurance';
import { useFleetInsurance } from './useFleetInsurance';

/**
 * Covers both ways the amount can be missing: never typed, and typed then
 * cleared. antd's parser turns an empty input into `0` rather than null
 * (`parseCurrencyFromAntd`), so "cleared" and "zero" are the same value here
 * and a $0/mo fleet policy is not a thing to persist (W1, John 2026-08-25).
 */
export const FLEET_AMOUNT_REQUIRED_MESSAGE =
  'Enter a monthly premium greater than $0.00 for your fleet policy.';

const TITLE_ID = 'fleetInsuranceModalTitle';

/**
 * antd's `InputNumber` is typed to hand back the value type it was given, but
 * it returns a *string* for input long enough to lose numeric precision. The
 * old `as number | null` cast let such a string reach `dollarsToCents`, which
 * type-guards on `number` and silently produced 0 cents — a wrong amount that
 * looked deliberate. Coerce explicitly instead, and treat anything that is not
 * a finite number as "no amount".
 */
const toDollarValue = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const SEGMENT_BASE =
  'rounded-full px-4 py-1 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-60';
const SEGMENT_SELECTED = 'bg-primary text-white';
const SEGMENT_UNSELECTED = 'bg-transparent text-primary';

interface FleetInsuranceModalProps {
  isOpen: boolean;
  /** Called on every close path — Cancel, X, backdrop, Escape, and a successful save. */
  onClose: () => void;
  /** The customer's assets — used to show what per-truck amounts sum to. */
  assets: Asset[];
}

/**
 * THT-675 — the editing half of the fleet insurance control.
 *
 * Lives in a modal rather than inline (collapsed-UI spec §2, revised
 * 2026-08-24): an inline expansion pushed the asset table down to ~1.5 visible
 * rows, which is the problem the revision exists to fix. A modal has zero
 * layout impact — the table never moves.
 *
 * Selecting a mode only stages it; the PATCH happens on Save, which is what
 * lets the fleet explainer (and the switch-back warning with the current
 * per-truck sum) be shown BEFORE the customer commits — the "intuitive"
 * requirement in the ticket.
 *
 * The input is DOLLARS. Cents exist only in the payload.
 */
export default function FleetInsuranceModal({
  isOpen,
  onClose,
  assets,
}: FleetInsuranceModalProps) {
  const {
    mode: savedMode,
    fleetAmountCents: savedAmountCents,
    isSaving,
    save,
  } = useFleetInsurance();

  const [draftMode, setDraftMode] = useState<InsurancePremiumMode>(savedMode);
  const [draftDollars, setDraftDollars] = useState<number | null>(
    centsToDollars(savedAmountCents)
  );
  const [showAmountError, setShowAmountError] = useState(false);
  const backdropPressRef = useRef(false);

  // The persisted record, readable by the seeding effect without becoming a
  // dependency of it — see below.
  const savedRef = useRef({ savedMode, savedAmountCents });
  savedRef.current = { savedMode, savedAmountCents };

  // Seed the draft from the persisted record on the closed -> open transition
  // ONLY. Re-seeding whenever the record changed also fired while the modal was
  // open, so any `refetchUser` landing mid-edit — another surface saving, an
  // auth refresh, a second tab — silently replaced what the customer had typed
  // (W4, John 2026-08-25). Closing still discards cleanly: the next open
  // re-seeds before anything is shown.
  useEffect(() => {
    if (!isOpen) return;
    setDraftMode(savedRef.current.savedMode);
    setDraftDollars(centsToDollars(savedRef.current.savedAmountCents));
    setShowAmountError(false);
  }, [isOpen]);

  // Escape is one of the four close paths (spec §3.2), suppressed mid-save for
  // the same reason the backdrop is.
  useEffect(() => {
    if (!isOpen || isSaving) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen) return null;

  const perAssetSumCents = sumAssetInsuranceCents(assets);
  const draftAmountCents = dollarsToCents(draftDollars);
  const isFleetDraft = draftMode === InsurancePremiumMode.Fleet;
  // A fleet policy always costs something, so 0 doubles as "no amount yet" —
  // which is why `savedAmountCents ?? 0` is a safe stand-in for a customer who
  // has never set one: 0 can never be what got persisted.
  const hasDraftAmount = draftAmountCents > 0;
  const isDirty =
    draftMode !== savedMode ||
    (isFleetDraft && draftAmountCents !== (savedAmountCents ?? 0));

  const selectMode = (next: InsurancePremiumMode) => {
    setDraftMode(next);
    setShowAmountError(false);
  };

  const closeIfIdle = () => {
    if (isSaving) return;
    onClose();
  };

  // A click is only a backdrop click when it BEGAN on the backdrop. Without
  // this, drag-selecting the amount and releasing outside the dialog closed the
  // modal and discarded the draft (M4).
  const onBackdropMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    backdropPressRef.current = event.target === event.currentTarget;
  };

  const onBackdropClick = () => {
    if (!backdropPressRef.current) return;
    backdropPressRef.current = false;
    closeIfIdle();
  };

  // Escape's keyboard equivalent for the X, which is an <svg role="button">.
  const onCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    closeIfIdle();
  };

  const onSave = async () => {
    // Guard on the parsed amount, not on `draftDollars === null`: clearing the
    // antd input yields 0, never null, so the old check let a $0/mo insurance
    // budget save silently (W1).
    if (isFleetDraft && !hasDraftAmount) {
      setShowAmountError(true);
      return;
    }
    // Close on success only: a failed PATCH keeps the modal open with the draft
    // intact so nothing the customer typed is lost (spec §3.2).
    const didSave = await save(draftMode, draftAmountCents);
    if (didSave) onClose();
  };

  const segmentClass = (selected: boolean) =>
    `${SEGMENT_BASE} ${selected ? SEGMENT_SELECTED : SEGMENT_UNSELECTED}`;

  return (
    <div
      data-testid="fleet-insurance-backdrop"
      className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50"
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="h-auto max-h-[90%] w-full max-w-[95%] overflow-y-auto overflow-x-hidden rounded-[4px] bg-white md:max-w-[520px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-[56px] w-full items-center justify-between p-4">
          <h2 id={TITLE_ID} className="text-[16px] font-[600] text-primary">
            {FLEET_INSURANCE_MODAL_TITLE}
          </h2>
          <X
            role="button"
            aria-label="Close"
            tabIndex={isSaving ? -1 : 0}
            className={
              isSaving
                ? 'pointer-events-none opacity-40'
                : 'cursor-pointer hover:opacity-80'
            }
            onClick={closeIfIdle}
            onKeyDown={onCloseKeyDown}
            size={24}
          />
        </div>

        <div className="flex w-full flex-col gap-y-2 border border-bordergray p-6">
          <p className="text-[14px] font-medium text-primary">
            {MODE_QUESTION_LABEL}
          </p>
          <div
            role="group"
            aria-label={MODE_QUESTION_LABEL}
            className="inline-flex w-fit max-w-full flex-row flex-wrap items-center gap-1 rounded-[20px] border-[1px] border-bordergray p-[3px]"
          >
            <button
              type="button"
              aria-pressed={!isFleetDraft}
              disabled={isSaving}
              onClick={() => selectMode(InsurancePremiumMode.PerAsset)}
              className={segmentClass(!isFleetDraft)}
            >
              {PER_ASSET_OPTION_LABEL}
            </button>
            <button
              type="button"
              aria-pressed={isFleetDraft}
              disabled={isSaving}
              onClick={() => selectMode(InsurancePremiumMode.Fleet)}
              className={segmentClass(isFleetDraft)}
            >
              {FLEET_OPTION_LABEL}
            </button>
          </div>

          {isFleetDraft ? (
            <div className="flex w-full max-w-[360px] flex-col gap-y-1">
              <label
                htmlFor="fleetInsurancePremium"
                className="text-[14px] font-medium text-[#858897]"
              >
                {FLEET_AMOUNT_LABEL}
              </label>
              <InputNumber
                id="fleetInsurancePremium"
                className="w-full rounded-md border border-gray-300 p-1 focus:outline-none"
                placeholder="Ex. $ 2,000"
                formatter={(v) => formatCurrencyForAntd(v)}
                parser={(v) => parseCurrencyFromAntd(v)}
                min={0}
                controls={false}
                disabled={isSaving}
                value={draftDollars}
                status={showAmountError ? 'error' : undefined}
                onChange={(value) => {
                  setDraftDollars(toDollarValue(value));
                  setShowAmountError(false);
                }}
              />
              {showAmountError && (
                <p className="text-sm text-red-500">
                  {FLEET_AMOUNT_REQUIRED_MESSAGE}
                </p>
              )}
              {/* The explainer quotes the number back, so it has nothing to
                  say until there is one — selecting Entire Fleet used to
                  announce "We'll budget $0.00/mo…" before the customer had
                  typed anything (M2). */}
              {hasDraftAmount && (
                <p className="text-[13px] text-graysecondary">
                  {fleetModeExplainer(draftAmountCents)}
                </p>
              )}
            </div>
          ) : (
            /* Spec §7/R2.2-R2.3 — one paragraph, not two: the description is
               appended to the switch-back warning rather than stacked under
               it (Enzo, 2026-08-24). Switching away from a saved fleet policy
               says what happens and then how to change it; with nothing to
               switch back from there is nothing to append to, so the
               description stands on its own. */
            <p className="text-[13px] text-graysecondary">
              {savedMode === InsurancePremiumMode.Fleet
                ? `${perAssetSwitchWarning(
                    perAssetSumCents
                  )} ${PER_ASSET_OPTION_DESCRIPTION}`
                : PER_ASSET_OPTION_DESCRIPTION}
            </p>
          )}
        </div>

        {/* Cancel then Save — neutral before accent, so the primary is on the
            right in a row and at the bottom once the pair stacks on mobile.
            Save stays rendered but disabled until there is something to
            persist (spec §3.2 — no more lone Cancel). */}
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:justify-between">
          <NeutralButton
            onClick={closeIfIdle}
            disabled={isSaving}
            addClass="rounded-full h-[48px] w-full sm:w-[220px]"
          >
            Cancel
          </NeutralButton>
          <AccentButton
            onClick={onSave}
            disabled={isSaving || !isDirty}
            addClass="rounded-full h-[48px] w-full sm:w-[220px] disabled:opacity-50"
          >
            {isSaving ? (
              <ClipLoader
                color="#1D213B"
                loading={isSaving}
                size={20}
                aria-label="Loading Spinner"
                data-testid="fleet-insurance-loader"
                className="flex h-full w-full justify-center"
              />
            ) : (
              'Save'
            )}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}
